import { GitBranch, Loader2, Webhook } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { handleError } from "../../lib/errors";
import { notify } from "../../lib/notify";
import { Service } from "../../types";
import { Button } from "../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";

interface AutoDeployPanelProps {
  service: Service;
  onServiceUpdate: () => void;
}

export function AutoDeployPanel({ service, onServiceUpdate }: AutoDeployPanelProps) {
  const savedEnabled = service.autoDeploy?.enabled ?? false;
  const savedBranch = service.autoDeploy?.branch ?? service.repository?.branch ?? "main";

  const [enabled, setEnabled] = useState(savedEnabled);
  const [branch, setBranch] = useState(savedBranch);
  const [saving, setSaving] = useState(false);

  // Sync local state when service reloads.
  useEffect(() => {
    setEnabled(service.autoDeploy?.enabled ?? false);
    setBranch(service.autoDeploy?.branch ?? service.repository?.branch ?? "main");
  }, [service.autoDeploy?.enabled, service.autoDeploy?.branch, service.repository?.branch]);

  const isDirty =
    enabled !== savedEnabled || (enabled && branch !== savedBranch);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.services.updateAutoDeploy(service.id, enabled, enabled ? branch : undefined);
      notify.success(
        enabled ? "Auto-deploy enabled" : "Auto-deploy disabled",
        {
          description: enabled
            ? `Pushes to "${branch}" will trigger a deploy automatically.`
            : "Pushes will no longer trigger deployments.",
        }
      );
      onServiceUpdate();
    } catch (err) {
      handleError(err, { scope: "AutoDeployPanel" });
      // Revert optimistic UI.
      setEnabled(savedEnabled);
      setBranch(savedBranch);
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setEnabled(savedEnabled);
    setBranch(savedBranch);
  };

  const hasRepo = !!service.repository?.url;

  return (
    <div className="space-y-4 pt-4">
      {/* Auto-deploy toggle */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Webhook className="h-4 w-4" />
            Auto Deploy
          </CardTitle>
          <CardDescription>
            Automatically deploy when you push to a branch on GitHub.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {!hasRepo ? (
            <p className="text-sm text-muted-foreground">
              Connect a Git repository to this service before enabling auto-deploy.
            </p>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Enable auto-deploy</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    A GitHub webhook will be registered on your repository.
                  </p>
                </div>
                <Switch
                  checked={enabled}
                  onCheckedChange={setEnabled}
                  disabled={saving}
                  aria-label="Enable auto-deploy"
                />
              </div>

              {enabled && (
                <div className="space-y-2">
                  <Label htmlFor="autodeploy-branch" className="flex items-center gap-1.5 text-sm">
                    <GitBranch className="h-3.5 w-3.5" />
                    Watch branch
                  </Label>
                  <Input
                    id="autodeploy-branch"
                    value={branch}
                    onChange={(e) => setBranch(e.target.value)}
                    placeholder="main"
                    disabled={saving}
                    className="font-mono"
                  />
                  <p className="text-xs text-muted-foreground">
                    Pushes to this branch will trigger a new deployment.
                  </p>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Repository info */}
      {hasRepo && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Repository</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="grid grid-cols-[120px_1fr] items-center gap-2">
              <span className="text-muted-foreground">URL</span>
              <a
                href={service.repository!.url}
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono truncate hover:underline"
              >
                {service.repository!.url}
              </a>
            </div>
            <div className="grid grid-cols-[120px_1fr] items-center gap-2">
              <span className="text-muted-foreground">Branch</span>
              <span className="font-mono">{service.repository!.branch || "—"}</span>
            </div>
            {service.autoDeploy?.enabled && (
              <div className="grid grid-cols-[120px_1fr] items-center gap-2">
                <span className="text-muted-foreground">Webhook</span>
                <span className="flex items-center gap-1.5 text-success">
                  <span className="inline-block h-2 w-2 rounded-full bg-success" />
                  Active
                </span>
              </div>
            )}
            {service.autoDeploy?.pendingDeploy && (
              <div className="mt-2 rounded-md bg-warning-bg px-3 py-2 text-xs text-warning-fg">
                A push arrived while a build was in progress — another deploy will start automatically when the current build finishes.
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Dirty-state save bar */}
      {isDirty && hasRepo && (
        <div className="sticky bottom-4 flex items-center justify-between rounded-lg bg-card px-4 py-3">
          <p className="text-sm text-muted-foreground">You have unsaved changes</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleDiscard} disabled={saving}>
              Discard
            </Button>
            <Button size="sm" onClick={handleSave} disabled={saving || !branch.trim()}>
              {saving && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
              Save
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
